import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import Icon from './Icon';

// One-line confirmation after a successful write ("Expense added").
// Lives above the routes, so a toast fired just before navigating back
// to the ledger survives the route change. The region is always in the
// DOM with role="status", so screen readers announce each new message.

const TOAST_MS = 2500;

type ShowToast = (message: string) => void;
const ToastCtx = createContext<ShowToast>(() => {});

export function useToast(): ShowToast {
  return useContext(ToastCtx);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<{ id: number; message: string } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  const show = useCallback<ShowToast>((message) => {
    setToast({ id: Date.now(), message });
  }, []);

  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        {toast && (
          <div key={toast.id} className="toast">
            <span className="toast-check" aria-hidden="true">
              <Icon name="check" size={15} stroke={2.6} />
            </span>
            {toast.message}
          </div>
        )}
      </div>
    </ToastCtx.Provider>
  );
}
